import { useEffect, useState } from "react";
import api, { apiError, fmtDate } from "@/lib/api";
import { SectionTitle } from "@/components/common";
import { toast } from "sonner";
import { Upload, Download, CheckCircle2, Loader2, Archive } from "lucide-react";

const inp = "w-full px-3 py-2.5 rounded-lg bg-white border border-[var(--border)] focus:border-[#FC82ED] outline-none text-sm";
const lab = "block text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] mb-1.5";

async function scarica(m) {
  try {
    const r = await api.get(`/modelli-documenti/${m.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data); const a = document.createElement("a");
    a.href = url; a.download = m.original_filename || "modello.docx"; a.click(); URL.revokeObjectURL(url);
  } catch (e) { toast.error(apiError(e)); }
}

function Carica({ tipi, onDone }) {
  const [f, setF] = useState({ tipo: "proposta_acquisto", nome: "", versione: "", valido_dal: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fk, setFk] = useState(0);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const invia = async () => {
    if (!file || !f.nome.trim() || !f.versione.trim() || !f.valido_dal) return toast.error("Compila tutti i campi e scegli il file .docx");
    const fd = new FormData(); Object.entries(f).forEach(([k, v]) => fd.append(k, v)); fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/modelli-documenti", fd);
      toast.success(`Modello caricato: ${data.segnaposti.length} segnaposti trovati`);
      setF({ ...f, nome: "", versione: "", valido_dal: "" }); setFile(null); setFk((n) => n + 1); onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card-immo p-5 mb-6" data-testid="modelli-carica">
      <h3 className="font-display font-bold text-sm mb-3">Carica un nuovo modello</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div><label className={lab}>Tipo</label>
          <select data-testid="modello-tipo" value={f.tipo} onChange={set("tipo")} className={inp}>
            {Object.entries(tipi).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select></div>
        <div><label className={lab}>Nome</label><input data-testid="modello-nome" value={f.nome} onChange={set("nome")} className={inp} /></div>
        <div><label className={lab}>Versione</label><input data-testid="modello-versione" value={f.versione} onChange={set("versione")} placeholder="es. 2027.1" className={inp} /></div>
        <div><label className={lab}>Valido dal</label><input data-testid="modello-valido-dal" type="date" value={f.valido_dal} onChange={set("valido_dal")} className={inp} /></div>
      </div>
      <div className="flex items-center gap-3 mt-3 flex-wrap">
        <input key={fk} data-testid="modello-file" type="file" accept=".docx" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm flex-1 min-w-[200px]" />
        <button data-testid="modello-carica-btn" onClick={invia} disabled={busy}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#111111] text-white text-sm font-semibold disabled:opacity-50">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Carica e attiva
        </button>
      </div>
      <p className="text-[11px] text-[var(--muted)] mt-2">I segnaposto nel formato {"{{ nome_campo }}"} vengono letti in automatico. Il nuovo modello diventa quello attivo; le versioni precedenti restano in archivio e i documenti già generati non cambiano.</p>
    </div>
  );
}

function ModelloCard({ m, onAttiva }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`p-4 ${m.attivo ? "bg-emerald-50/60" : ""}`} data-testid={`modello-${m.id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[180px]">
          <div className="font-semibold text-sm">{m.nome} <span className="mono text-xs text-[var(--muted)]">v{m.versione}</span></div>
          <div className="text-[11px] text-[var(--muted)]">Valido dal {fmtDate(m.valido_dal)} · caricato il {fmtDate(m.created_at)} da {m.created_by_name || "—"}</div>
        </div>
        {m.attivo ? <span data-testid={`modello-attivo-${m.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500 text-white text-[11px] font-bold"><CheckCircle2 size={12} /> Attivo</span>
          : <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-slate-500 text-[11px] font-bold"><Archive size={12} /> Archivio</span>}
        <button data-testid={`modello-segnaposti-${m.id}`} onClick={() => setOpen((o) => !o)} className="text-xs font-semibold underline">{m.segnaposti.length} segnaposti</button>
        <button data-testid={`modello-download-${m.id}`} onClick={() => scarica(m)} className="p-2 rounded-lg border border-[var(--border)] bg-white"><Download size={14} /></button>
        {!m.attivo && <button data-testid={`modello-attiva-${m.id}`} onClick={() => onAttiva(m)} className="px-3 py-2 rounded-xl border border-[var(--border)] bg-white text-xs font-semibold">Rendi attivo</button>}
      </div>
      {open && (
        <div className="flex flex-wrap gap-1.5 mt-3" data-testid={`modello-lista-segnaposti-${m.id}`}>
          {m.segnaposti.map((s) => <span key={s.nome} title={s.sezione} className="mono text-[11px] px-2 py-0.5 rounded-md bg-white border border-[var(--border)]">{s.nome}</span>)}
        </div>
      )}
    </div>
  );
}

export default function ModelliDocumenti() {
  const [d, setD] = useState(null);
  const load = () => api.get("/modelli-documenti").then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  const attiva = async (m) => {
    if (!window.confirm(`Rendere attivo "${m.nome}" v${m.versione}? Il modello attuale andrà in archivio.`)) return;
    try { await api.post(`/modelli-documenti/${m.id}/attiva`); toast.success("Modello attivato"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  if (!d) return <div className="skeleton h-40 rounded-2xl" />;
  return (
    <div className="fade-up" data-testid="modelli-documenti-page">
      <SectionTitle sub="Un solo modello attivo per tipo. Ogni documento generato ricorda la versione usata.">Modelli documenti</SectionTitle>
      <Carica tipi={d.tipi} onDone={load} />
      {Object.entries(d.tipi).map(([k, label]) => {
        const lista = d.modelli.filter((m) => m.tipo === k);
        return (
          <div key={k} className="mb-5" data-testid={`modelli-tipo-${k}`}>
            <h3 className="font-display font-bold text-sm mb-2">{label}</h3>
            {lista.length === 0 ? <div className="card-immo p-5 text-sm text-[var(--muted)]">Nessun modello caricato.</div>
              : <div className="card-immo p-0 divide-y divide-[var(--border)] overflow-hidden">{lista.map((m) => <ModelloCard key={m.id} m={m} onAttiva={attiva} />)}</div>}
          </div>
        );
      })}
    </div>
  );
}
